/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DTypeRepository, DTypeToGoLiveRequest } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import { catchError, map, Subject, takeUntil, throwError } from 'rxjs';
import { RequestGoLiveBody, RequestGoLiveDialogData } from './request-go-live-dialog.types';
import { DialogsService } from '../dialogs.service';
import { DTypeService } from '../../core/dtype/dtype.service';
import { LocalizePipe } from '../../core/pipes/localize.pipe';

@Component({
    selector: 'request-go-live-dialog',
    templateUrl: './request-go-live-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .request-go-live-dialog-panel {
                @screen md {
                    @apply w-128;
                }

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RequestGoLiveDialogComponent implements OnInit, OnDestroy {

    date: FormControl = new FormControl<string>(undefined);
    observations: FormControl = new FormControl<string>(undefined);
    requestDate: FormControl = new FormControl<string>(undefined);
    requestUser: FormControl = new FormControl<string>(undefined);
    datemin: string;
    readOnly: boolean = false;

    private unsubscribe = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: RequestGoLiveDialogData,
        public matDialogRef: MatDialogRef<RequestGoLiveDialogComponent>,
        private dtypeService: DTypeService,
        private transloco: TranslocoService,
        private dialogsService: DialogsService,
        private localize: LocalizePipe
    ) { }

    ngOnInit() {
        if (this.data?.dtypeToGoLiveRequest) {
            this.readOnly = true;
            this.requestDate.setValue(this.getDateFromTimestamp(this.data.dtypeToGoLiveRequest.timestamp));
            this.date.setValue(this.data.dtypeToGoLiveRequest.requirementsDate);
            this.requestUser.setValue(this.data.userName ?? this.data.userId);
            this.observations.setValue(this.data.dtypeToGoLiveRequest.observations);
        }

        const todayDate = new Date();
        this.datemin = format(todayDate.setDate(todayDate .getDate() + 1), 'yyyy-MM-dd');

        this.date.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
            this.dateControlChanged(value);
        });
    }

    ngOnDestroy() {
        this.unsubscribe.complete();
    }

    closeDialog(result?: DTypeRepository): void {
        this.matDialogRef.close(result);
    }

    openDatepickerOnClick(datepicker: MatDateRangePicker<Date>) {
        if (!datepicker.opened && !this.readOnly) {
            datepicker.open();
        }
    }

    save() {
        const inner: DTypeToGoLiveRequest = {
            userId: this.data.userId,
            requirementsDate: this.date.value ?? null,
            observations: this.observations.value ?? null,
            relativeUrl: this.data.relativeUrl
        };
        const body: RequestGoLiveBody = {
            uri: this.data.uri,
            dtypeToGoLiveRequest: inner,
            cancelMail: false
        };
        this.dtypeService.flushDTypeToGoLiveRequest(body).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                });
                console.error(error);
                this.closeDialog();
                return throwError(() => error.error);
            }),
            map(reply => reply.repository)
        ).subscribe((repo) => {
            this.closeDialog(repo);
        });
    }

    private dateControlChanged(value: Date | string) {
        if (value instanceof Date) {
            this.date.setValue(format(value, 'yyyy-MM-dd'), { emitEvent: false });
        }
    }

    private getDateFromTimestamp(time: string) {
        return time?.split('T')[0];
    }

}
